import { CloseOutlined } from '@ant-design/icons'
import { useEventEmitter } from 'ahooks'
import { Spin } from 'antd'
import { last } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'

import {
  useGetAnnouncementPostsQuery,
  useGetViewerQuery,
  useViewerAnnouncementUpdateMutation,
} from '@/generated/graphql'
import { PostCommentCreate } from '@/modules/post/post-card/post-comments/post-comment-create.component'

import { AnnouncementCard } from './announcement-card.component'
import s from './announcement.module.less'

export const AnnouncementContainer = () => {
  const fetchCount = 6
  const { t } = useTranslation()
  const ref = React.useRef<HTMLDivElement>(null)
  const { data: viewerData } = useGetViewerQuery({
    variables: {
      showAnnouncement: true,
    },
  })
  const { data, fetchMore, refetch, loading } = useGetAnnouncementPostsQuery({
    skip: !viewerData || !viewerData.viewer.shouldBeAnnounced,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      pagination: {
        last: fetchCount,
      },
    },
  })

  const [updateAnnouncement] = useViewerAnnouncementUpdateMutation()
  const onChangeContent$ = useEventEmitter<string | null>()

  const shouldBeAnnounced = viewerData?.viewer.shouldBeAnnounced
  const posts = data?.posts.edges.map(({ node }) => node).reverse() || []

  React.useLayoutEffect(() => {
    const node = ref.current
    if (posts.length <= fetchCount && node && node.scrollHeight > node.clientHeight) {
      node.scrollTop = node.scrollHeight
    }
  }, [posts])

  React.useEffect(() => {
    if (shouldBeAnnounced) {
      refetch()
    }
  }, [shouldBeAnnounced])

  const pageInfo = data?.posts.pageInfo || {
    hasPreviousPage: null,
    hasNextPage: null,
    startCursor: null,
    endCursor: null,
  }

  if (!shouldBeAnnounced) {
    return null
  }

  const header = (
    <div className={s.header}>
      <span>{`What's new`}</span>
      <CloseOutlined onClick={() => updateAnnouncement({ variables: { shouldBeAnnounced: false } })} />
    </div>
  )

  const loadingComponent = loading && (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Spin delay={300} size='large' />
    </div>
  )

  const content = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column-reverse' }} className={s.timeline} id='announcementDiv'>
        <InfiniteScroll
          style={{ display: 'flex', flexDirection: 'column-reverse' }}
          dataLength={posts.length}
          loader={loadingComponent}
          hasMore={!!pageInfo?.hasPreviousPage}
          inverse={true}
          scrollableTarget='announcementDiv'
          next={() =>
            fetchMore({
              variables: {
                pagination: {
                  last: fetchCount,
                  before: pageInfo.startCursor,
                },
              },
            })
          }
        >
          {posts.map((post) => (
            <AnnouncementCard post={post} key={post.postId} />
          ))}
        </InfiniteScroll>
      </div>
      {posts.length > 0 && (
        <div className={s.comment}>
          <PostCommentCreate
            post={last(posts)!}
            placeholder={t('post.announcement.replyComment')}
            autoFocus
            onChangeContent$={onChangeContent$}
          />
        </div>
      )}
    </>
  )

  return (
    <div className={s.container}>
      {header}
      {content}
    </div>
  )
}
